import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { ProductImage } from '../../ui/ProductImage';
import { canSubscribe } from '../../core/rules/features';
import { bundleIncomplete } from '../../core/rules/cartBundles';
import { isSoldOut } from '../../core/rules/stock';
import { useFeatures } from '../../hooks/useFeatures';
import { shopLabels } from '../../data/shopLabels';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { euro } from '../../core/format/currency';
import { ease } from '../../core/format/motion';
import type { CartLine } from '../../core/types/cart';
type Props = {
  routineId: string;
  lines: CartLine[];
};

/** 40px raakvlak, zoals in een gewone regel. */
const iconBtn = 'flex h-10 w-10 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink';

/**
 * Een protocol in de mand: één blok met naam, totaal (de som, geen bundelkorting) en één keuze
 * voor eenmalig of bijvullen. De producten staan ingeklapt; uitgeklapt kun je er één uithalen,
 * dan wordt de rest gewone regels (core/rules/cartBundles.ts).
 */
export function CartBundle({
  routineId,
  lines
}: Props) {
  const {
    routineById
  } = useCatalog();
  const {
    removeBundle,
    remove,
    setBundleSubscribe
  } = useCartActions();
  const {
    closeCart
  } = useOverlayActions();
  const features = useFeatures();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const routine = routineById(routineId);
  const name = routine?.name ?? 'Protocol';
  const total = lines.reduce((n, l) => n + l.product.price * l.qty, 0);
  const refillable = lines.filter(l => canSubscribe(features, l.product));
  const subscribed = refillable.length > 0 && refillable.every(l => l.subscribe);
  const incomplete = routine ? bundleIncomplete(routine.productIds, lines) : false;
  const soldOut = lines.some(l => isSoldOut(l.product));
  return <motion.li layout={!reduce} initial={reduce ? false : {
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={reduce ? {
    opacity: 0
  } : {
    opacity: 0,
    x: 24,
    transition: {
      duration: 0.18,
      ease
    }
  }} transition={{
    duration: 0.24,
    ease
  }} className="py-5" data-id="element-6813" data-mp="3d7gfjsnxh7c8">
      <div className="flex items-start justify-between gap-3" data-id="element-6814" data-mp="epaw4aeodfgi">
        <div className="min-w-0" data-id="element-6815" data-mp="equf48rpu4lz">
          <p className="text-[13px] text-clay" data-id="element-6816" data-mp="wshx9fpmyw">Protocol · {lines.length} producten</p>
          <h3 className="mt-0.5 font-display text-[1.15rem] leading-snug text-ink" data-id="element-6817" data-mp="4yokera2x933">{name}</h3>
        </div>
        <div className="flex shrink-0 items-start gap-1" data-id="element-6818" data-mp="soiwv7qhkvmt">
          <span className="pt-1 font-display text-lg text-ink" data-id="element-6819" data-mp="smzftex0agjz">{euro(total)}</span>
          <button type="button" onClick={() => removeBundle(routineId)} aria-label={`Verwijder ${name} uit je winkelmand`} className={`-mr-2 -mt-1 ${iconBtn}`} data-id="element-6820" data-mp="xgtxydwyqngy">
            <XIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6821" data-mp="2d7moq6ui3l9a" />
          </button>
        </div>
      </div>

      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls={listId} className="group mt-3 flex w-full items-center gap-2 text-left" data-id="element-6822" data-mp="2qwycnf8ef34r">
        <span className="flex -space-x-2" aria-hidden="true" data-id="element-6823" data-mp="1z967vn2xcedf">
          {lines.slice(0, 5).map(l => <ProductImage key={l.product.id} src={l.product.image} alt="" width={40} className="h-12 w-10 shrink-0 bg-sand p-1 ring-2 ring-shell" data-id="element-6824" data-mp="1ua1wqqwfw6kg" />)}
        </span>
        <span className="ml-2 text-[13px] text-muted group-hover:text-ink" data-id="element-6825" data-mp="2373ymuccf8ix">{open ? 'Verberg de producten' : 'Bekijk de producten'}</span>
        <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-200 ease-soft ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-6826" data-mp="3enu9iqrpooiv" />
      </button>

      {open && <ul id={listId} className="mt-3 divide-y divide-line border-y border-line" data-id="element-6827" data-mp="isqzaxhyru8i">
          {lines.map(l => <li key={l.product.id} className="flex items-center gap-3 py-2.5" data-id="element-6828" data-mp="np7mnphca4kj">
              <div className="min-w-0 flex-1" data-id="element-6829" data-mp="sqwyv8gasoa7">
                <Link to={`/webshop/${l.product.id}`} onClick={closeCart} className="text-[14px] leading-snug text-ink underline-offset-4 hover:underline" data-id="element-6830" data-mp="sniwi07mwf2r">
                  {l.product.name}
                </Link>
                <p className="text-[12px] text-muted" data-id="element-6831" data-mp="16ha4v59mpsww">
                  {isSoldOut(l.product) ? shopLabels.soldOutShort : l.product.brand}
                  {l.qty > 1 && ` · ${l.qty}×`}
                </p>
              </div>
              <span className="text-[13px] tabular-nums text-muted" data-id="element-6832" data-mp="1gg97o77pqubz">{euro(l.product.price * l.qty)}</span>
              <button type="button" onClick={() => remove(l.product.id)} aria-label={shopLabels.remove(l.product)} className={`-mr-2 ${iconBtn}`} data-id="element-6833" data-mp="1ki3423avlc6c">
                <XIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6834" data-mp="34wr5mp443bm8" />
              </button>
            </li>)}
        </ul>}
      {open && <p className="mt-2 text-[12px] text-muted" data-id="element-6835" data-mp="2vz74rha5vhc5">Haal je er één uit, dan staat de rest als losse producten in je mand.</p>}

      {(incomplete || soldOut) && <p className="mt-3 text-[13px] leading-snug text-clay" data-id="element-6836" data-mp="3omtel79zfi7g">
          {soldOut ? 'Een product is inmiddels uitverkocht.' : 'Niet compleet: een product was uitverkocht.'}{' '}
          <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4" data-id="element-6837" data-mp="3jqcklwg3q9mg">
            Vraag {founder.firstName} om een alternatief
            <span className="sr-only" data-id="element-6838" data-mp="39xx1uwwzdh0i">{shopLabels.viaWhatsApp}</span>
          </a>
        </p>}

      {refillable.length > 0 && <fieldset className="mt-4 grid grid-cols-2 gap-1.5" data-id="element-6839" data-mp="6hq6resmss3c">
          <legend className="sr-only" data-id="element-6840" data-mp="6g5ankqo513d">Eenmalig of bijvullen: {name}</legend>
          {[{
        value: false,
        label: 'Eenmalig'
      }, {
        value: true,
        label: 'Bijvullen'
      }].map(o => {
        const on = subscribed === o.value;
        return <label key={o.label} className={`flex cursor-pointer items-center justify-center rounded-full px-3 py-2.5 text-[13px] transition-[background-color,color,box-shadow] duration-150 ease-soft [&:has(:focus-visible)]:outline [&:has(:focus-visible)]:outline-2 [&:has(:focus-visible)]:outline-offset-2 [&:has(:focus-visible)]:outline-clay ${on ? 'bg-ink text-shell' : 'text-ink ring-1 ring-line hover:ring-ink/40'}`} data-id="element-6841" data-mp="1qyzeh1t77tms">
                <input type="radio" name={`bundle-${routineId}`} checked={on} onChange={() => setBundleSubscribe(routineId, o.value)} className="sr-only" data-id="element-6842" data-mp="1czzrdylc3msq" />
                {o.label}
              </label>;
      })}
          <p className="col-span-2 mt-1 text-[12px] leading-snug text-muted" data-id="element-6843" data-mp="3hhp67ptqh70j">Bijvullen: zelfde prijs, {policyCopy.subscriptionPerks}.</p>
        </fieldset>}
    </motion.li>;
}